import './react-phone-number-input.css';
import { useState, useImperativeHandle, forwardRef } from 'react';
import buscarCep from 'src/actions/chats/buscarCep';
import Iconify from 'src/components/Iconify';
import PhoneInput from 'react-phone-number-input';
import { maskCPF, maskDate } from 'src/utils/functions';
import { useSnackbar } from 'notistack';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';
import { Label } from 'src/@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Separator } from 'src/@/components/ui/separator';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';

// ----------------------------------------------------------------------

function NovoContatoForm({
    showSaveButton,
    callbackAddContact,
    hideSelectEmpresa,
    showSignupButton,
    loadingNovoContato,
    hideActionButtons
}, ref) {
    const { enqueueSnackbar } = useSnackbar();
    const [contato, setContato] = useState({
        nome: '',
        email: '',
        telefone: '',
        dataNascimento: '',
        cpf: ''
    });
    const [endereco, setEndereco] = useState({
        cep: '',
        rua: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        complemento: ''
    });
    const [camposPersonalizados, setCamposPersonalizados] = useState([]);
    const [campoPersonalizado, setCampoPersonalizado] = useState({
        nome: '',
        valor: ''
    });

    const handleCallbackAddContact = () => {
        const data = { contato, endereco, camposPersonalizados };
        if (data.contato.nome === '' || data.contato.telefone === '' || data.contato.telefone.length < 10) {
            enqueueSnackbar('Preencha o nome e o telefone corretamente', { variant: 'error' });
            return;
        }
        callbackAddContact(data);
        clearFields();
    };

    useImperativeHandle(ref, () => ({
        submit: handleCallbackAddContact
    }));

    function clearFields() {
        setContato({ nome: '', email: '', telefone: '', dataNascimento: '', cpf: '' });
        setEndereco({ cep: '', rua: '', numero: '', bairro: '', cidade: '', estado: '', complemento: '' });
    }

    const handleOnBlurSearchCep = async (zipCode) => {
        try {
            const address = await buscarCep(zipCode);
            if (!address.erro) {
                setEndereco({
                    ...endereco,
                    cep: address.cep,
                    rua: address.logradouro,
                    bairro: address.bairro,
                    cidade: address.localidade,
                    estado: address.uf,
                });
            }
        } catch (_) {}
    };

    function addNovoCampoPersonalizado() {
        if (campoPersonalizado.nome === '' || campoPersonalizado.valor === '') {
            enqueueSnackbar('Preencha os campos', { variant: 'error' });
            return;
        }
        setCamposPersonalizados([...camposPersonalizados, { ...campoPersonalizado }]);
        setCampoPersonalizado({ nome: '', valor: '' });
    }

    function removeCampoPersonalizado(index) {
        setCamposPersonalizados(camposPersonalizados.filter((_, i) => i !== index));
    }

    return (
        <div className="flex flex-col space-y-5">
            {/* Telefone */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                    Telefone <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                    international
                    defaultCountry="BR"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring"
                    placeholder="Insira o número"
                    value={contato.telefone}
                    onChange={value => setContato({ ...contato, telefone: value })}
                />
            </div>

            <Separator className="bg-border/60" />

            {/* Nome + Data Nascimento */}
            <div className="grid grid-cols-5 gap-3">
                <div className="col-span-3 space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                        Nome <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        placeholder="Ex: João da Silva"
                        onChange={e => setContato({ ...contato, nome: e.target.value })}
                        value={contato.nome}
                        className="h-10 bg-background"
                    />
                </div>
                <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                        Data Nascimento
                    </Label>
                    <Input
                        placeholder="DD/MM/AAAA"
                        onChange={e => setContato({ ...contato, dataNascimento: e.target.value })}
                        value={maskDate(contato.dataNascimento)}
                        className="h-10 bg-background"
                    />
                </div>
            </div>

            {/* Email + CPF */}
            <div className="grid grid-cols-5 gap-3">
                <div className="col-span-3 space-y-2">
                    <Label className="text-sm font-medium text-foreground">Email</Label>
                    <Input
                        type="email"
                        placeholder="email@exemplo.com"
                        onChange={e => setContato({ ...contato, email: e.target.value })}
                        value={contato.email}
                        className="h-10 bg-background"
                    />
                </div>
                <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium text-foreground">CPF</Label>
                    <Input
                        placeholder="000.000.000-00"
                        onChange={e => setContato({ ...contato, cpf: e.target.value })}
                        value={maskCPF(contato.cpf)}
                        className="h-10 bg-background"
                    />
                </div>
            </div>

            <Separator className="bg-border/60" />

            {/* Accordions */}
            <Accordion type="multiple" className="w-full">
                {/* Endereço */}
                <AccordionItem value="endereco" className="border-b-0">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline py-3">
                        <div className="flex items-center gap-2">
                            <Iconify icon="mdi:map-marker-outline" className="w-4 h-4 text-violet-600" />
                            Endereço
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-4">
                        <div className="w-1/2">
                            <Label className="text-xs font-medium text-muted-foreground">CEP</Label>
                            <Input
                                placeholder="00000-000"
                                onChange={e => setEndereco({ ...endereco, cep: e.target.value })}
                                onBlur={e => handleOnBlurSearchCep(e.target.value)}
                                value={endereco.cep}
                                className="h-9 bg-background mt-1"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">Rua</Label>
                            <Input
                                onChange={e => setEndereco({ ...endereco, rua: e.target.value })}
                                value={endereco.rua}
                                className="h-9 bg-background"
                            />
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                            <div className="col-span-1 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Número</Label>
                                <Input
                                    onChange={e => setEndereco({ ...endereco, numero: e.target.value })}
                                    value={endereco.numero}
                                    className="h-9 bg-background"
                                />
                            </div>
                            <div className="col-span-3 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Bairro</Label>
                                <Input
                                    onChange={e => setEndereco({ ...endereco, bairro: e.target.value })}
                                    value={endereco.bairro}
                                    className="h-9 bg-background"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-5 gap-2">
                            <div className="col-span-2 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Complemento</Label>
                                <Input
                                    onChange={e => setEndereco({ ...endereco, complemento: e.target.value })}
                                    value={endereco.complemento}
                                    className="h-9 bg-background"
                                />
                            </div>
                            <div className="col-span-2 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Cidade</Label>
                                <Input
                                    onChange={e => setEndereco({ ...endereco, cidade: e.target.value })}
                                    value={endereco.cidade}
                                    className="h-9 bg-background"
                                />
                            </div>
                            <div className="col-span-1 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Estado</Label>
                                <Input
                                    onChange={e => setEndereco({ ...endereco, estado: e.target.value })}
                                    value={endereco.estado}
                                    className="h-9 bg-background"
                                />
                            </div>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Campos personalizados */}
                <AccordionItem value="campos-personalizados" className="border-b-0">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline py-3">
                        <div className="flex items-center gap-2">
                            <Iconify icon="mdi:form-textbox" className="w-4 h-4 text-violet-600" />
                            Campos personalizados
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-4">
                        {camposPersonalizados.map((item, index) => (
                            <div key={index} className="flex items-end gap-2">
                                <div className="flex-1 space-y-1">
                                    <Label className="text-xs font-medium text-muted-foreground">{item.nome}</Label>
                                    <Input
                                        onChange={e => {
                                            const novosCampos = camposPersonalizados.map((campo, i) =>
                                                i === index ? { ...campo, valor: e.target.value } : campo
                                            );
                                            setCamposPersonalizados(novosCampos);
                                        }}
                                        value={item?.valor}
                                        className="h-9 bg-background"
                                    />
                                </div>
                                <Button
                                    onClick={() => removeCampoPersonalizado(index)}
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                                >
                                    <Iconify icon="mdi:trash-can-outline" className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}

                        <Separator className="bg-border/40" />

                        <div className="flex items-end gap-2">
                            <div className="w-[35%] space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Nome do campo</Label>
                                <Input
                                    onChange={e => setCampoPersonalizado({ ...campoPersonalizado, nome: e.target.value })}
                                    value={campoPersonalizado.nome}
                                    className="h-9 bg-background"
                                />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs font-medium text-muted-foreground">Valor</Label>
                                <Input
                                    onChange={e => setCampoPersonalizado({ ...campoPersonalizado, valor: e.target.value })}
                                    value={campoPersonalizado.valor}
                                    className="h-9 bg-background"
                                />
                            </div>
                            <Button
                                onClick={addNovoCampoPersonalizado}
                                size="icon"
                                className="h-9 w-9 bg-green-500 hover:bg-green-600 text-white shrink-0"
                            >
                                <Iconify icon="mdi:plus" className="w-4 h-4" />
                            </Button>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* Botões de ação */}
            {!hideActionButtons && showSaveButton && (
                <div className="pt-2">
                    <Button
                        onClick={handleCallbackAddContact}
                        variant="outline"
                        className="flex items-center gap-2 text-sm"
                    >
                        <Iconify icon="mdi:plus" className="w-4 h-4" />
                        Adicionar
                    </Button>
                </div>
            )}

            {!hideActionButtons && showSignupButton && (
                <div className="pt-2">
                    <ButtonLoading
                        loading={loadingNovoContato}
                        onClick={handleCallbackAddContact}
                        className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white rounded-md flex items-center justify-center gap-2 transition-all font-medium shadow-sm shadow-violet-500/20 hover:shadow-violet-500/40"
                    >
                        <Iconify icon="mdi:account-plus-outline" className="w-[18px] h-[18px]" />
                        Criar Contato
                    </ButtonLoading>
                </div>
            )}
        </div>
    );
}

export default forwardRef(NovoContatoForm);
